import {
  FINANCING_LABELS,
  QUOTE_STATUS_LABELS,
  UNIT_STATUS_LABELS,
  UNIT_TYPE_LABELS,
  type DealDto,
  type QuoteDto,
  type UnitDto,
} from '@performance-crm/shared';
import { CURRENCY_SYMBOL } from '@/lib/format';
import { excelDate, excelDateTime, type SheetCell } from '@/lib/spreadsheet';

const num = (v: string | number | null | undefined): SheetCell =>
  v === null || v === undefined || v === '' ? null : Number(v);
const leadLink = (leadId: string) => `${window.location.origin}/leads/${leadId}`;
const DEAL_STATE: Record<DealDto['state'], string> = {
  ACTIVO: 'Activa',
  DEVUELTO: 'Devuelta',
  ANULADO: 'Anulada',
};

/** Hoja «Stock»: una fila por unidad con sus datos, estado y quién la tiene (misma tabla que la pantalla). */
export function stockSheet(units: UnitDto[], tz: string) {
  return {
    name: 'Stock',
    header: [
      'Unidad',
      'Torre / etapa',
      'Piso',
      'Tipo',
      'Tipología',
      'Dormitorios',
      'Baños',
      'Área total (m²)',
      'Área techada (m²)',
      'Área libre (m²)',
      'Vista',
      'Precio de lista',
      'Precio de venta',
      'Moneda',
      'Estado',
      'Bloqueada',
      'Motivo del bloqueo',
      'Cliente',
      'Asesor',
      'Proceso',
      'Actualizada',
    ],
    rows: units.map((u): SheetCell[] => [
      u.code,
      u.subdivisionName ?? '',
      u.floor,
      UNIT_TYPE_LABELS[u.unitType] ?? u.unitType,
      u.typology ?? '',
      u.bedrooms,
      num(u.bathrooms),
      num(u.areaTotal),
      num(u.areaBuilt),
      num(u.areaFree),
      u.view ?? '',
      num(u.priceList),
      num(u.priceSale),
      CURRENCY_SYMBOL[u.currency] ?? u.currency,
      UNIT_STATUS_LABELS[u.commercialStatus] ?? u.commercialStatus,
      u.isBlacklisted ? 'Sí' : 'No',
      u.blockReason ?? '',
      // El cliente solo llega a quien puede ver el lead (P17); el asesor que atiende, siempre.
      u.holder?.contactName ?? '',
      u.holder?.advisorName ?? '',
      u.holder ? (u.holder.dealType === 'VENTA' ? 'Venta' : 'Separación') : '',
      excelDateTime(u.updatedAt, tz),
    ]),
  };
}

/** Hoja «Proformas»: la lista filtrada con precios desglosados, estado y fechas en la zona de la empresa. */
export function quotesSheet(quotes: QuoteDto[], tz: string) {
  return {
    name: 'Proformas',
    header: [
      'Código',
      'Cliente',
      'Proyecto',
      'Unidad',
      'Tipo de unidad',
      'Precio de lista',
      'Descuento',
      'Precio',
      'Moneda',
      'Financiamiento',
      'Estado',
      'Emitida',
      'Vence',
      'Asesor',
      'Emitida por',
      'Notas',
      'Anulada el',
      'Motivo de anulación',
      'Convertida el',
      'Enlace al lead',
    ],
    rows: quotes.map((q): SheetCell[] => [
      q.code,
      q.contactName,
      q.projectName,
      q.unitCode ?? '',
      q.unitType ? (UNIT_TYPE_LABELS[q.unitType] ?? q.unitType) : '',
      num(q.priceList),
      num(q.discount),
      num(q.price),
      CURRENCY_SYMBOL[q.currency] ?? q.currency,
      q.financingType ? (FINANCING_LABELS[q.financingType] ?? q.financingType) : '',
      QUOTE_STATUS_LABELS[q.status] ?? q.status,
      excelDateTime(q.createdAt, tz),
      excelDate(q.expiresAt, tz),
      q.advisorName ?? '',
      q.createdByName ?? '',
      q.notes ?? '',
      excelDateTime(q.annulledAt, tz),
      q.annulReason ?? '',
      excelDateTime(q.convertedAt, tz),
      leadLink(q.leadId),
    ]),
  };
}

/** Hoja «Procesos»: separaciones y ventas con la reserva y el precio en columnas separadas. */
export function dealsSheet(deals: DealDto[], tz: string) {
  return {
    name: 'Procesos',
    header: [
      'Tipo',
      'Cliente',
      'Proyecto',
      'Unidades',
      'Fecha de separación',
      'Fecha de venta',
      'Reserva',
      'Moneda de la reserva',
      'Precio',
      'Moneda del precio',
      'Modalidad',
      'Banco',
      'N.º de contrato',
      'Minuta firmada',
      'Estado',
      'Devuelta el',
      'Motivo de caída',
      'Notas de la devolución',
      'Anulada el',
      'Motivo de anulación',
      'Proforma',
      'Asesor',
      'Registrada',
      'Enlace al lead',
    ],
    rows: deals.map((d): SheetCell[] => [
      d.type === 'VENTA' ? 'Venta' : 'Separación',
      d.contactName,
      d.projectName,
      d.units.map((u) => u.code).join(', '),
      excelDate(d.startedAt, tz),
      excelDate(d.finishedAt, tz),
      d.type === 'SEPARACION' ? num(d.amountInitial) : null,
      d.type === 'SEPARACION' ? (CURRENCY_SYMBOL[d.initialCurrency] ?? d.initialCurrency) : '',
      d.type === 'VENTA' ? num(d.amountTotal) : null,
      CURRENCY_SYMBOL[d.currency] ?? d.currency,
      d.contractMode ? (FINANCING_LABELS[d.contractMode] ?? d.contractMode) : '',
      d.bank ?? '',
      d.contractNumber ?? '',
      excelDate(d.minuteSignedAt, tz),
      DEAL_STATE[d.state],
      excelDate(d.refundedAt, tz),
      d.dropReason ?? '',
      d.refundNotes ?? '',
      excelDateTime(d.annulledAt, tz),
      d.annulReason ?? '',
      d.quoteCode ?? '',
      d.advisorName ?? '',
      excelDateTime(d.createdAt, tz),
      leadLink(d.leadId),
    ]),
  };
}
